import { apiFetch, authHeaders } from '../client';
import { SharePointListClient, odataEscape, type ListItem } from '../SharePointListClient';
import { createInventarioForArticulo } from './inventario';

/**
 * 99.ABM_Articulos — unified catalogue for both Productos (Cotizable_ART="SI")
 * and Servicios (Cotizable_ART="NO"). Most numeric fields are stored as
 * text-encoded numbers; IVA is stored as "10,5" / "21" (Spanish decimal),
 * so we round-trip that formatting verbatim.
 */
export type ArticulosFields = {
  Title?: string | null;
  Articulo_ART?: string | null;
  Codigo_ART?: string | null;
  Modelo_ART?: string | null;
  Descripcion_ART?: string | null;
  Categoria_ART?: string | null;
  CuentaContable_ART?: string | null;
  /** Product taxonomy (Hornos/Accesorios/Repuestos), independent of the
   *  accounting Cuenta Contable. Drives gift eligibility + inventory tipo;
   *  null for Servicios. */
  TipoProducto_ART?: string | null;
  Concat_ART?: string | null;
  ValorUn_ART?: string | null;
  ValorUnDolar_ART?: string | null;
  IVA_ART?: '10,5' | '21' | string | null;
  /**
   * MX-only: supplier-cost base for the Nova Factura Interna formula
   * (`costo_contable * Multiplicador Costo Contable * (1 + IVA)`).
   * Stored as comma-decimal text like the other money fields. Null on
   * AR rows; the dialog field is gated to MX via `useActiveCountry`.
   */
  CostoContable_ART?: string | null;
  Cotizable_ART?: 'SI' | 'NO' | string | null;
  Comprable_ART?: 'SI' | 'NO' | string | null;
  LlevaNroSerie_ART?: 'SI' | 'NO' | string | null;
  ImpactaStock_ART?: 'SI' | 'NO' | string | null;
  Electrico_ART?: 'SI' | 'NO' | string | null;
  /** Meses de garantía del producto (null = sin duración cargada). */
  GarantiaMeses_ART?: string | null;
  Status_ART?: 'Activo' | 'Inactivo' | string | null;
  /**
   * Graph drive/item pointer for the new image pipeline. `ImagenDriveId_Art`
   * uses a lowercase `t` — SharePoint internal name mismatch with the
   * display name. The other three suffix with `_ART`.
   */
  ImagenDriveId_Art?: string | null;
  ImagenItemId_ART?: string | null;
  ImagenNombre_ART?: string | null;
  ImagenMimeType_ART?: string | null;
};

export type ArticuloItem = ListItem<ArticulosFields>;

/**
 * Serialized article — `LlevaNroSerie_ART = 'SI'` (inherited from the cuenta
 * contable's `NroSerie_CC`). Serialized units (hornos, etc.) must be one
 * unit per cotización line so each physical unit gets its own `NroSerie_DO`
 * at despacho. Cotización add auto-splits cantidad>1 into N qty-1 lines.
 */
export function articuloLlevaNroSerie(a: ArticuloItem): boolean {
  return (a.fields.LlevaNroSerie_ART ?? '').toString().trim().toUpperCase() === 'SI';
}

export const articulosClient = new SharePointListClient<ArticulosFields>('ABM_Articulos');

export type CotizableFlag = 'SI' | 'NO';
const bool = (b: boolean): 'SI' | 'NO' => (b ? 'SI' : 'NO');

/**
 * Scans all articulos of a given "cotizable" flag.
 * Productos = cotizable_ART 'SI'
 * Servicios = cotizable_ART 'NO' (AND comprable_ART 'SI' per PowerApps convention,
 *   but the combobox filters by cotizable alone; we mirror that.)
 *
 * Relies on `Status_ART` being indexed in SharePoint. We rely on the
 * Cotizable_ART filter being valid OData (it's a string eq, always works).
 */
export async function listArticulosByCotizable(
  cotizable: CotizableFlag,
): Promise<ArticuloItem[]> {
  return articulosClient.listItems({
    filter: `fields/Cotizable_ART eq ${odataEscape(cotizable)}`,
    top: 200,
  });
}

/**
 * Active articulos — used by Campañas and other generic pickers.
 * Requires `Status_ART` and `Articulo_ART` to be indexed in SharePoint.
 */
export async function listActiveArticulos(): Promise<ArticuloItem[]> {
  return articulosClient.listItems({
    filter: "fields/Status_ART eq 'Activo'",
    orderBy: 'fields/Articulo_ART asc',
  });
}

/**
 * Active AND quotable articulos — drives the Nueva Cotización picker.
 * Mirrors the PowerApps filter exactly:
 *   Filter('99.ABM_Articulos', Status_ART='Activo' AND Cotizable_ART='SI').
 * Needs both `Status_ART` and `Cotizable_ART` indexed in SharePoint.
 */
export async function listActiveCotizableArticulos(): Promise<ArticuloItem[]> {
  return articulosClient.listItems({
    filter:
      "fields/Status_ART eq 'Activo' and fields/Cotizable_ART eq 'SI'",
    orderBy: 'fields/Articulo_ART asc',
  });
}

/**
 * Active AND purchasable articulos — drives the Nueva Compra
 * line-item cascade (Categoria → CuentaContable → Articulo). Mirrors
 * the PowerApps filter:
 *   Filter('99.ABM_Articulos', Status_ART='Activo' AND Comprable_ART='SI').
 * If `Comprable_ART` isn't indexed on a given tenant, Graph will 400 —
 * we fall back to filtering by Status_ART (indexed) and narrowing in
 * memory.
 */
export async function listActiveCompraleArticulos(): Promise<ArticuloItem[]> {
  try {
    return await articulosClient.listItems({
      filter:
        "fields/Status_ART eq 'Activo' and fields/Comprable_ART eq 'SI'",
      orderBy: 'fields/Articulo_ART asc',
    });
  } catch (err) {
    console.warn('[articulos] compound filter failed, falling back to client-side:', err);
    const all = await articulosClient.listItems({
      filter: "fields/Status_ART eq 'Activo'",
      orderBy: 'fields/Articulo_ART asc',
    });
    return all.filter((a) => a.fields.Comprable_ART === 'SI');
  }
}

/**
 * Fixed product-type → Cuenta Contable mapping (AR chart-of-accounts merge,
 * 2026-06). The article dialog picks the PRODUCT TYPE (Hornos/Accesorios/
 * Repuestos); the accounting CC is derived from it — all per the client's
 * chart-of-accounts Excel: Accesorios & Repuestos book to "Insumos"
 * (Respuestos / Accesorios / Insumos), Hornos books to "Mercaderias"
 * (Mercaderia-Productos). The product type is persisted separately on
 * `articles.tipo_producto`, so the product distinction (picker / inventory /
 * gift gate) survives the accounting merge.
 */
export const PRODUCT_TYPE_TO_CC: Record<string, string> = {
  Hornos: 'Mercaderias',
  Accesorios: 'Insumos',
  Repuestos: 'Insumos',
};

/**
 * Companion to PRODUCT_TYPE_TO_CC — the categoría axis each product type books
 * to in the new chart. Sent alongside the cuenta so an article's categoría
 * (`articles.category_id`) always tracks its cuenta. It also disambiguates the
 * cuenta resolver: "Mercaderias" exists under both Mercaderia-Productos and
 * Mercaderia-Reparaciones, so without this hint a new Horno could resolve to
 * the wrong gl_account.
 */
export const PRODUCT_TYPE_TO_CATEGORIA: Record<string, string> = {
  Hornos: 'Mercaderia-Productos',
  Accesorios: 'Respuestos / Accesorios / Insumos',
  Repuestos: 'Respuestos / Accesorios / Insumos',
};

/**
 * Cuenta contable + categoría que le corresponden a un tipo de producto, según
 * el plan de cuentas de CADA país.
 *
 * En AR el plan se fusionó (2026-06): Hornos → Mercaderias, Accesorios y
 * Repuestos → Insumos. **México nunca hizo esa fusión**: sus cuentas se llaman
 * literalmente `Hornos` / `Accesorios` / `Repuestos`, todas bajo la categoría
 * `Activos`. Aplicar el mapa argentino en MX dejaba al artículo sin cuenta
 * (el `.find()` no encontraba "Mercaderias") y, peor, sin heredar
 * `ImpactaStock` ni `NroSerie` — un horno quedaba sin descontar stock y sin
 * pedir número de serie en el despacho. Pasó con 2 productos MX cargados
 * después de la fusión (BOLEADORA DE MASA, Amasadora OPERA 15k).
 */
export function cuentaParaTipoProducto(
  tipoProducto: string,
  pais: string,
): { cuenta: string; categoria: string | null } {
  if (pais === 'MX') {
    // La cuenta se llama igual que el tipo; todas cuelgan de 'Activos'.
    return { cuenta: tipoProducto, categoria: 'Activos' };
  }
  return {
    cuenta: PRODUCT_TYPE_TO_CC[tipoProducto] ?? tipoProducto,
    categoria: PRODUCT_TYPE_TO_CATEGORIA[tipoProducto] ?? null,
  };
}

export type ArticuloPayload = {
  articulo: string;
  codigo: string;
  modelo?: string | null;
  descripcion?: string | null;
  cuentaContable?: string | null;
  /** Product taxonomy (Hornos/Accesorios/Repuestos). Persisted to
   *  `articles.tipo_producto`; independent of `cuentaContable`. */
  productType?: string | null;
  /**
   * Optional Categoria override. When omitted, `createArticulo` defaults
   * to `'Activos'` to preserve the pre-existing behavior for Productos.
   * Servicios pass the user-selected `Categoria_ART` verbatim.
   */
  categoria?: string | null;
  /** Decimal as the user typed it. Store as text with Spanish comma. */
  valorUn?: number | null;
  valorUnDolar?: number | null;
  /**
   * MX-only supplier-cost base. Pass `null` to clear, omit to leave the
   * existing value untouched. AR callers should never pass this — it
   * stays `null` on AR rows.
   */
  costoContable?: number | null;
  /**
   * IVA rate as a country-formatted string ('21' / '10,5' for AR;
   * '16' / '8' for MX). PowerApps writes `'-'` for Servicios (no fixed
   * IVA — invoice-level tax only). Productos use the real rate.
   */
  iva: string;
  cotizable: CotizableFlag;
  comprable: boolean;
  llevaNroSerie: boolean;
  impactaStock: boolean;
  electrico?: boolean;
  /** Meses de garantía; null borra. Sólo se manda si el caller lo pasa. */
  garantiaMeses?: number | null;
};

function formatNumberAR(n: number | null | undefined): string | null {
  if (n == null || Number.isNaN(n)) return null;
  return String(n).replace('.', ',');
}

function fieldsFromPayload(p: ArticuloPayload): Partial<ArticulosFields> {
  const out: Partial<ArticulosFields> = {
    Articulo_ART: p.articulo.trim(),
    Codigo_ART: p.codigo.trim(),
    Modelo_ART: p.modelo?.trim() ?? null,
    Descripcion_ART: p.descripcion?.trim() ?? null,
    CuentaContable_ART: p.cuentaContable?.trim() || null,
    Concat_ART: `${p.codigo.trim()} - ${p.articulo.trim()}`,
    ValorUn_ART: formatNumberAR(p.valorUn),
    ValorUnDolar_ART: formatNumberAR(p.valorUnDolar),
    IVA_ART: p.iva,
    ...(p.costoContable !== undefined
      ? { CostoContable_ART: formatNumberAR(p.costoContable) }
      : {}),
    Cotizable_ART: p.cotizable,
    Comprable_ART: bool(p.comprable),
    LlevaNroSerie_ART: bool(p.llevaNroSerie),
    ImpactaStock_ART: bool(p.impactaStock),
  };
  // Only emit Electrico_ART when the caller passed it. ServicioDialog
  // doesn't carry this flag and we don't want it to silently clear an
  // existing electric flag on edit.
  if (p.electrico !== undefined) {
    out.Electrico_ART = bool(p.electrico);
  }
  if (p.garantiaMeses !== undefined) {
    out.GarantiaMeses_ART = p.garantiaMeses != null ? String(p.garantiaMeses) : null;
  }
  // Product type (Hornos/Accesorios/Repuestos), persisted separately from the
  // accounting CC. Only emit when the caller passes it (ServicioDialog doesn't).
  if (p.productType !== undefined) {
    out.TipoProducto_ART = p.productType?.trim() || null;
  }
  // Only emit Categoria_ART when the caller explicitly passes one. Keeps
  // existing callers (Productos) from accidentally clearing the field
  // on edit — the create-side default lives in `createArticulo`.
  if (p.categoria !== undefined) {
    out.Categoria_ART = p.categoria?.trim() || null;
  }
  return out;
}

export async function createArticulo(payload: ArticuloPayload): Promise<ArticuloItem> {
  const created = await articulosClient.createItem({
    Title: '[sumar]',
    Status_ART: 'Activo',
    // Fallback only — Servicios and Productos both send their categoría
    // explicitly (the latter via PRODUCT_TYPE_TO_CATEGORIA), and the spread
    // below overrides this. Fall back to null, never the retired 'Activos'.
    Categoria_ART: payload.categoria?.trim() || null,
    ...fieldsFromPayload(payload),
  });

  // Create the companion Inventario row so stock management works end-to-end.
  // Best-effort: log but don't fail the main operation if this breaks.
  try {
    await createInventarioForArticulo({
      articuloId: created.id,
      articuloNombre: payload.articulo.trim(),
      // Inventory tipo follows the PRODUCT type (Hornos/Accesorios/Repuestos),
      // not the accounting CC (which may be the merged "Insumos").
      tipoInventario:
        payload.productType?.trim() || payload.cuentaContable?.trim() || 'Sin Categoría',
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[articulos] companion Inventario row could not be created', err);
  }

  return created;
}

export async function updateArticulo(
  id: string,
  payload: ArticuloPayload,
): Promise<ArticuloItem> {
  return articulosClient.updateItem(id, fieldsFromPayload(payload));
}

export async function softDeleteArticulo(id: string): Promise<ArticuloItem> {
  return articulosClient.updateItem(id, { Status_ART: 'Inactivo' });
}

/** Parse stored text number (may use "," as decimal) back to number. */
export function parseArticuloNumber(raw: string | number | null | undefined): number | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') return raw;
  const n = Number(raw.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

// ---------------------------------------------------------------------------
// Image helpers (new Graph pipeline; legacy base64 is server-side fallback)
// ---------------------------------------------------------------------------

export type ArticuloImageUploadResult = {
  driveItemId: string;
  fileName: string;
  size: number;
  mimeType: string;
};

/**
 * Upload an image for a given articulo. Backend writes to the
 * ArticuloImagenes drive and patches `ImagenDriveId_Art` + friends on the
 * row, so subsequent `articuloImageUrl(id)` calls return the new image.
 */
export async function uploadArticuloImage(
  articuloId: string,
  file: File,
): Promise<ArticuloImageUploadResult> {
  const form = new FormData();
  form.append('file', file);

  const res = await fetch(`/api/articulos/${articuloId}/image`, {
    method: 'POST',
    body: form,
    headers: authHeaders(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let payload: { error?: string; message?: string } | undefined;
    try {
      payload = text ? JSON.parse(text) : undefined;
    } catch {
      /* fall through */
    }
    throw new Error(
      payload?.message ??
        payload?.error ??
        `No se pudo subir la imagen (HTTP ${res.status}).`,
    );
  }
  return (await res.json()) as ArticuloImageUploadResult;
}

export async function deleteArticuloImage(articuloId: string): Promise<void> {
  await apiFetch(`/api/articulos/${articuloId}/image`, { method: 'DELETE' });
}

/**
 * Raw path of the authenticated image endpoint. Useful when you need the
 * URL for deduping / cache-busting, but **do not** use this as an `<img
 * src>` — the browser won't attach the bearer token and the request will
 * 401. Use `fetchArticuloImageBlobUrl()` instead for rendering.
 */
export function articuloImageUrl(articuloId: string): string {
  return `/api/articulos/${articuloId}/image`;
}

/**
 * Fetch the authenticated image and return a blob URL suitable for `<img
 * src>`. Returns `null` when the row has no image (new columns empty AND
 * no legacy base64 row). Caller is responsible for `URL.revokeObjectURL`
 * when the URL is replaced or unmounted.
 */
export async function fetchArticuloImageBlobUrl(
  articuloId: string,
): Promise<string | null> {
  const res = await fetch(articuloImageUrl(articuloId), {
    headers: authHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`No se pudo cargar la imagen (HTTP ${res.status}).`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * True iff the articulo row has the new image columns populated. Useful as
 * a fast client-side probe before rendering an `<img>` — a true value
 * guarantees the image exists; a false value might still have a legacy
 * base64 entry, so render with a graceful `onError` fallback.
 */
export function hasArticuloImage(item: ArticuloItem): boolean {
  return Boolean((item.fields.ImagenItemId_ART ?? '').trim());
}
